import { useCallback, useMemo, useState, type ReactNode } from "react"

import type { Offer } from "@/api/hooks/offers"
import type { OfferColumn } from "../lib/columns"
import { PINNED_DYNAMIC_COLUMN } from "../lib/columns"
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "../lib/formatters"

/**
 * Offers table with sortable headers, HTML5 drag-and-drop column
 * reorder, profit coloring, and status pills. Parity:
 * `offers/index.html:1542-1900`.
 *
 * Fixed columns (Name + Actions) are always rendered first. Dynamic
 * columns follow the user's `columnOrder`. Status is pinned in the
 * dynamic set (cannot be dragged or receive drops).
 */

// Status pill styling — parity: `offers/index.html:1684-1695`
const STATUS_STYLES: Record<string, string> = {
  active: "bg-[#166534] text-[#4ade80]",
  attention: "bg-[#78350f] text-[#fbbf24]",
  processing: "bg-[#1e3a5f] text-[#60a5fa]",
  archived: "bg-[#374151] text-[#9ca3af]",
  inactive: "bg-[#7f1d1d] text-[#f87171]",
  failed: "bg-[#7f1d1d] text-[#f87171]",
}

function StatusPill({ status }: { status: string }) {
  const key = (status || "").toLowerCase()
  const cls = STATUS_STYLES[key] || STATUS_STYLES.inactive
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}
    >
      {status || "Unknown"}
    </span>
  )
}

// Metric cell formatter
const CURRENCY_COLS = new Set([
  "revenue", "rpm", "rpc", "cost", "profit", "cpa", "cpc", "cpm",
])
const PERCENT_COLS = new Set(["flow_1_to_2", "roi", "ctr", "cvr"])

function metricCell(colId: string, value: number | null | undefined): ReactNode {
  if (CURRENCY_COLS.has(colId)) return formatCurrency(value)
  if (PERCENT_COLS.has(colId)) return formatPercent(value)
  return formatNumber(value)
}

function getMetricValue(
  offer: Offer,
  colId: string,
): number | null | undefined {
  const m = offer.metrics
  if (!m) return null
  // Handle gclicks/g_clicks fallback — parity: lines 1700-1701
  if (colId === "gclicks") return m.gclicks ?? m.g_clicks
  return (m as unknown as Record<string, number | null | undefined>)[colId]
}

interface OffersTableProps {
  columns: readonly OfferColumn[]
  columnOrder: string[]
  hiddenColumns: Set<string>
  offers: Offer[]
  sortBy: string
  sortOrder: "asc" | "desc"
  search: string
  isLoading: boolean
  isError: boolean
  errorMessage?: string
  onSort: (key: string, columnType: "string" | "number") => void
  onColumnOrderChange: (order: string[]) => void
  onEdit?: (offer: Offer) => void
  onDuplicate?: (offer: Offer) => void
  onArchiveRestore: (offer: Offer) => void
  onCopyLink: (offer: Offer) => void
  onViewLink: (offer: Offer) => void
  onKeywords?: (offer: Offer) => void
}

export function OffersTable({
  columns,
  columnOrder,
  hiddenColumns,
  offers,
  sortBy,
  sortOrder,
  search,
  isLoading,
  isError,
  errorMessage,
  onSort,
  onColumnOrderChange,
  onEdit,
  onDuplicate,
  onArchiveRestore,
  onCopyLink,
  onViewLink,
  onKeywords,
}: OffersTableProps) {
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  const columnsById = useMemo(() => {
    const m = new Map<string, OfferColumn>()
    for (const c of columns) m.set(c.id, c)
    return m
  }, [columns])

  const orderedDynamicCols = useMemo(
    () =>
      columnOrder
        .filter((id) => !hiddenColumns.has(id))
        .map((id) => columnsById.get(id))
        .filter(Boolean) as OfferColumn[],
    [columnOrder, columnsById, hiddenColumns],
  )

  // Client-side search filter (current page only). Parity: lines 1445-1518.
  const filteredOffers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return offers
    return offers.filter((o) => {
      return (
        (o.name || "").toLowerCase().includes(q) ||
        (o.network || "").toLowerCase().includes(q) ||
        (o.status || "").toLowerCase().includes(q) ||
        (o.target_url || "").toLowerCase().includes(q) ||
        (o.partner_domain || "").toLowerCase().includes(q)
      )
    })
  }, [offers, search])

  // Total column count: Name + Actions + dynamic columns
  const totalCols = 2 + orderedDynamicCols.length

  // Drag handlers
  const handleDragStart = useCallback(
    (e: React.DragEvent, colId: string) => {
      if (colId === PINNED_DYNAMIC_COLUMN) {
        e.preventDefault()
        return
      }
      e.dataTransfer.setData("text/plain", colId)
      e.dataTransfer.effectAllowed = "move"
      setDragId(colId)
    },
    [],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent, colId: string) => {
      if (colId === PINNED_DYNAMIC_COLUMN) return
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      if (dragId !== colId) setDragOverId(colId)
    },
    [dragId],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent, colId: string) => {
      e.preventDefault()
      setDragOverId(null)
      const fromId = e.dataTransfer.getData("text/plain")
      setDragId(null)
      if (
        !fromId ||
        fromId === colId ||
        fromId === PINNED_DYNAMIC_COLUMN ||
        colId === PINNED_DYNAMIC_COLUMN
      )
        return
      const fromIdx = columnOrder.indexOf(fromId)
      const toIdx = columnOrder.indexOf(colId)
      if (fromIdx === -1 || toIdx === -1) return
      const next = columnOrder.slice()
      const [rem] = next.splice(fromIdx, 1)
      next.splice(toIdx, 0, rem)
      // Ensure status stays first
      const statusIdx = next.indexOf(PINNED_DYNAMIC_COLUMN)
      if (statusIdx > 0) {
        next.splice(statusIdx, 1)
        next.unshift(PINNED_DYNAMIC_COLUMN)
      }
      onColumnOrderChange(next)
    },
    [columnOrder, onColumnOrderChange],
  )

  const handleDragEnd = useCallback(() => {
    setDragId(null)
    setDragOverId(null)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOverId(null)
  }, [])

  // Show inline status in the name cell when the status column is hidden.
  const statusVisible =
    columnOrder.includes("status") && !hiddenColumns.has("status")

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center text-white">
        Loading offers...
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full flex-col">
      <div className="relative min-h-0 min-w-0 flex-1 overflow-auto overscroll-x-contain rounded-md border border-[#4a4a4a] bg-[#2a2a2a] [-webkit-overflow-scrolling:touch] [scrollbar-gutter:stable]">
      <table className="w-max min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-[#2a2a2a]">
          <tr>
            {/* Fixed: Name */}
            <th
              className="sticky left-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-[#4a4a4a] bg-[#2a2a2a] px-3 py-2 text-left text-xs font-semibold text-[#b0b0b0]"
              style={{ minWidth: "250px" }}
              onClick={() => onSort("name", "string")}
            >
              <span className="inline-flex items-center gap-1">
                <span>Name</span>
                {sortBy === "name" && (
                  <span className="text-[0.7rem] text-[#8b9aff]" aria-hidden>
                    {sortOrder === "asc" ? "\u25B2" : "\u25BC"}
                  </span>
                )}
              </span>
            </th>
            {/* Fixed: Actions */}
            <th
              className="whitespace-nowrap border-b border-[#4a4a4a] bg-[#2a2a2a] px-3 py-2 text-left text-xs font-semibold text-[#b0b0b0]"
              style={{ minWidth: "120px" }}
            >
              Actions
            </th>
            {/* Dynamic columns */}
            {orderedDynamicCols.map((col) => {
              const isPinned = col.id === PINNED_DYNAMIC_COLUMN
              const isActiveSort = sortBy === col.id
              const draggable = !isPinned
              return (
                <th
                  key={col.id}
                  className={[
                    "cursor-pointer select-none whitespace-nowrap border-b border-[#4a4a4a] bg-[#2a2a2a] px-3 py-2 text-left text-xs font-semibold text-[#b0b0b0]",
                    dragId === col.id ? "opacity-40" : "",
                    dragOverId === col.id
                      ? "outline outline-1 outline-[#667eea]"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                  draggable={draggable}
                  onClick={() => onSort(col.id, col.type)}
                  onDragStart={(e) => handleDragStart(e, col.id)}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col.id)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="inline-flex items-center gap-1">
                    {draggable && (
                      <span
                        className="cursor-grab text-[#666]"
                        aria-hidden
                        onClick={(e) => e.stopPropagation()}
                      >
                        {"\u2630"}
                      </span>
                    )}
                    <span>{col.label}</span>
                    {isActiveSort && (
                      <span
                        className="text-[0.7rem] text-[#8b9aff]"
                        aria-hidden
                      >
                        {sortOrder === "asc" ? "\u25B2" : "\u25BC"}
                      </span>
                    )}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {isError ? (
            <tr>
              <td
                colSpan={totalCols}
                className="p-4 text-center text-[#f87171]"
              >
                {errorMessage || "Failed to load offers"}
              </td>
            </tr>
          ) : filteredOffers.length === 0 ? (
            <tr>
              <td
                colSpan={totalCols}
                className="p-4 text-center text-[#b0b0b0]"
              >
                {search.trim()
                  ? "No offers match your search."
                  : "No offers yet. Create one to get started."}
              </td>
            </tr>
          ) : (
            filteredOffers.map((offer) => (
              <tr
                key={offer.id}
                className="hover:bg-[#333]"
              >
                {/* Name cell */}
                <td className="sticky left-0 z-[5] whitespace-nowrap border-b border-[#3a3a3a] bg-[#2a2a2a] px-3 py-3 font-semibold text-[#e0e0e0]">
                  <span>{offer.name}</span>
                  {/* Show inline status pill if status column is hidden */}
                  {!statusVisible && (
                    <span className="ml-2">
                      <StatusPill status={offer.status} />
                    </span>
                  )}
                </td>
                {/* Actions cell */}
                <td className="whitespace-nowrap border-b border-[#3a3a3a] px-3 py-3">
                  <div className="flex items-center gap-1">
                    <ActionButton
                      label="Copy TikTok destination URL"
                      emoji={"\uD83D\uDD17"}
                      onClick={() => onCopyLink(offer)}
                    />
                    {onEdit && (
                      <ActionButton
                        label="Edit offer"
                        emoji={"\u270F\uFE0F"}
                        onClick={() => onEdit(offer)}
                      />
                    )}
                    {(offer.target_url || offer.partner_domain) && (
                      <ActionButton
                        label="View"
                        emoji={"\uD83D\uDD0E"}
                        onClick={() => onViewLink(offer)}
                      />
                    )}
                    {onKeywords && (
                      <ActionButton
                        label="Keywords"
                        emoji={"\uD83D\uDCC2"}
                        onClick={() => onKeywords(offer)}
                      />
                    )}
                    {onDuplicate && (
                      <ActionButton
                        label="Duplicate"
                        emoji={"\uD83D\uDCC4"}
                        onClick={() => onDuplicate(offer)}
                      />
                    )}
                    <ActionButton
                      label={
                        offer.status === "Archived" ? "Restore" : "Archive"
                      }
                      emoji={
                        offer.status === "Archived" ? "\u21A9\uFE0F" : "\uD83D\uDCE6"
                      }
                      onClick={() => onArchiveRestore(offer)}
                    />
                  </div>
                </td>
                {/* Dynamic metric cells */}
                {orderedDynamicCols.map((col) => {
                  if (col.id === "status") {
                    return (
                      <td
                        key={col.id}
                        className="whitespace-nowrap border-b border-[#3a3a3a] px-3 py-3 text-[#e0e0e0]"
                      >
                        <StatusPill status={offer.status} />
                      </td>
                    )
                  }
                  const value = getMetricValue(offer, col.metricKey || col.id)
                  // Profit coloring — parity: lines 1883-1887
                  const profitStyle =
                    col.id === "profit" && value != null
                      ? {
                          color:
                            value > 0
                              ? "#4ade80"
                              : value < 0
                                ? "#f87171"
                                : undefined,
                        }
                      : undefined
                  return (
                    <td
                      key={col.id}
                      className="whitespace-nowrap border-b border-[#3a3a3a] px-3 py-3 text-right text-[#e0e0e0]"
                      style={profitStyle}
                    >
                      {metricCell(col.id, value)}
                    </td>
                  )
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}

/** Small action button with tooltip. */
function ActionButton({
  label,
  emoji,
  onClick,
}: {
  label: string
  emoji: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#555] bg-[#3a3a3a] text-sm transition-colors hover:border-[#8b9aff] hover:bg-[#4a4a4a]"
      aria-label={label}
    >
      <span aria-hidden>{emoji}</span>
    </button>
  )
}
